import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { coursesAPI, usersAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Users, 
  BookOpen, 
  Edit2, 
  Trash2, 
  UserPlus,
  Calendar,
  GraduationCap,
  ChevronRight,
  MoreVertical,
  Eye
} from 'lucide-react';

export default function CoursesPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    codigo: '',
    periodo: '',
    max_estudiantes: 50
  });
  
  // Students management
  const [courseStudents, setCourseStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const response = await coursesAPI.getAll(params);
      setCourses(response.data);
    } catch (error) {
      toast.error('Error al cargar cursos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleCreateCourse = async () => {
    try {
      await coursesAPI.create(formData);
      toast.success('Curso creado exitosamente');
      setShowCreateModal(false);
      setFormData({ nombre: '', descripcion: '', codigo: '', periodo: '', max_estudiantes: 50 });
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear curso');
    }
  };

  const handleUpdateCourse = async () => {
    try {
      await coursesAPI.update(selectedCourse.id, formData);
      toast.success('Curso actualizado');
      setShowEditModal(false);
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('¿Estás seguro de archivar este curso?')) return;
    try {
      await coursesAPI.delete(courseId);
      toast.success('Curso archivado');
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al archivar');
    }
  };

  const openEditModal = (course) => {
    setSelectedCourse(course);
    setFormData({
      nombre: course.nombre,
      descripcion: course.descripcion || '',
      codigo: course.codigo || '',
      periodo: course.periodo || '',
      max_estudiantes: course.max_estudiantes || 50
    });
    setShowEditModal(true);
  };

  const openStudentsModal = async (course) => {
    setSelectedCourse(course);
    try {
      const response = await coursesAPI.getStudents(course.id);
      setCourseStudents(response.data.students);
      setShowStudentsModal(true);
    } catch (error) {
      toast.error('Error al cargar estudiantes');
    }
  };

  const openAddStudentsModal = async () => {
    try {
      // Obtener alumnos del tenant que no están en el curso
      const response = await usersAPI.getAll({ role: 'alumno' });
      const currentStudentIds = courseStudents.map(s => s.id);
      const available = response.data.filter(u => !currentStudentIds.includes(u.id));
      setAvailableStudents(available);
      setSelectedStudentIds([]);
      setShowAddStudentsModal(true);
    } catch (error) {
      toast.error('Error al cargar estudiantes disponibles');
    }
  };

  const handleAddStudents = async () => {
    if (selectedStudentIds.length === 0) {
      toast.warning('Selecciona al menos un estudiante');
      return;
    }
    try {
      await coursesAPI.addStudents(selectedCourse.id, selectedStudentIds);
      toast.success(`${selectedStudentIds.length} estudiantes agregados`);
      setShowAddStudentsModal(false);
      // Recargar estudiantes del curso
      const response = await coursesAPI.getStudents(selectedCourse.id);
      setCourseStudents(response.data.students);
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar estudiantes');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('¿Remover este estudiante del curso?')) return;
    try {
      await coursesAPI.removeStudent(selectedCourse.id, studentId);
      toast.success('Estudiante removido');
      setCourseStudents(prev => prev.filter(s => s.id !== studentId));
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al remover estudiante');
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      activo: { variant: 'default', label: 'Activo', className: 'bg-green-500' },
      inactivo: { variant: 'secondary', label: 'Inactivo', className: 'bg-gray-500' },
      archivado: { variant: 'outline', label: 'Archivado', className: 'bg-red-500' }
    };
    const config = variants[status] || variants.activo;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredAvailableStudents = availableStudents.filter(s => 
    !studentSearchTerm || 
    `${s.nombre} ${s.apellido} ${s.email}`.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-blue-600" />
            Gestión de Cursos
          </h1>
          <p className="text-gray-600 mt-1">
            Administra tus cursos y estudiantes asignados
          </p>
        </div>
        
        {['instructor', 'admin', 'super_admin'].includes(user?.role) && (
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Curso
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">Activos</SelectItem>
                <SelectItem value="inactivo">Inactivos</SelectItem>
                <SelectItem value="archivado">Archivados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Courses Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      ) : courses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-16 h-16 mx-auto text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No hay cursos</h3>
            <p className="mt-2 text-gray-500">
              {searchTerm || statusFilter 
                ? 'No se encontraron cursos con los filtros aplicados'
                : 'Crea tu primer curso para empezar'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <Card key={course.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{course.nombre}</CardTitle>
                    {course.codigo && (
                      <CardDescription className="mt-1">
                        Código: {course.codigo}
                      </CardDescription>
                    )}
                  </div>
                  {getStatusBadge(course.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {course.descripcion && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {course.descripcion}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  {course.periodo && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {course.periodo}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {course.estudiantes_count} / {course.max_estudiantes}
                  </div>
                </div>
                
                {course.instructor_nombre && (
                  <div className="text-sm">
                    <span className="text-gray-500">Instructor:</span>{' '}
                    <span className="font-medium">{course.instructor_nombre}</span>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1"
                    onClick={() => openStudentsModal(course)}
                  >
                    <Users className="w-4 h-4" />
                    Estudiantes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(course)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteCourse(course.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Course Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Curso</DialogTitle>
            <DialogDescription>
              Completa la información del curso
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nombre del Curso *</label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Enfermería Clínica 2026"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Código</label>
              <Input
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                placeholder="Ej: ENF-301"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Período</label>
              <Input
                value={formData.periodo}
                onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                placeholder="Ej: 2026-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripción</label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows={3}
                placeholder="Descripción del curso..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Máximo de Estudiantes</label>
              <Input
                type="number"
                value={formData.max_estudiantes}
                onChange={(e) => setFormData({ ...formData, max_estudiantes: parseInt(e.target.value) || 50 })}
                min={1}
                max={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCourse} disabled={!formData.nombre}>
              Crear Curso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Course Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Curso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nombre del Curso *</label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Código</label>
              <Input
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Período</label>
              <Input
                value={formData.periodo}
                onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripción</label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Máximo de Estudiantes</label>
              <Input
                type="number"
                value={formData.max_estudiantes}
                onChange={(e) => setFormData({ ...formData, max_estudiantes: parseInt(e.target.value) || 50 })}
                min={1}
                max={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateCourse} disabled={!formData.nombre}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Students Modal */}
      <Dialog open={showStudentsModal} onOpenChange={setShowStudentsModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Estudiantes - {selectedCourse?.nombre}
            </DialogTitle>
            <DialogDescription>
              {courseStudents.length} de {selectedCourse?.max_estudiantes} estudiantes
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Button onClick={openAddStudentsModal} className="w-full gap-2 mb-4">
              <UserPlus className="w-4 h-4" />
              Agregar Estudiantes
            </Button>
            
            {courseStudents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay estudiantes en este curso</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {courseStudents.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">
                        {student.nombre} {student.apellido}
                      </p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStudent(student.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Students Modal */}
      <Dialog open={showAddStudentsModal} onOpenChange={setShowAddStudentsModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Agregar Estudiantes</DialogTitle>
            <DialogDescription>
              Selecciona los estudiantes a agregar al curso
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar estudiantes..."
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {selectedStudentIds.length > 0 && (
              <div className="text-sm text-blue-600 font-medium">
                {selectedStudentIds.length} estudiantes seleccionados
              </div>
            )}
            
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {filteredAvailableStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay estudiantes disponibles</p>
                </div>
              ) : (
                filteredAvailableStudents.map((student) => (
                  <div
                    key={student.id}
                    onClick={() => toggleStudentSelection(student.id)}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedStudentIds.includes(student.id)
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div>
                      <p className="font-medium">
                        {student.nombre} {student.apellido}
                      </p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedStudentIds.includes(student.id)
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-300'
                    }`}>
                      {selectedStudentIds.includes(student.id) && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStudentsModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddStudents} disabled={selectedStudentIds.length === 0}>
              Agregar ({selectedStudentIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
